import { ExportButton } from '@/common/buttons/ExportButton';
import { Grid } from '@/common/grid/Grid';
import { useModuleFromRoute } from '@/common/hooks/route/useModuleFromRoute';
import { useNavState } from '@/common/navState/NavStateContext';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { Title } from '@/common/title/Title';
import { DateUtils } from '@/common/utils/DateUtils';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { Box } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

export const ReallocTrxViewer = () => {
  const { navState } = useNavState();
  const rsrc = useRsrc();
  const module = useModuleFromRoute();

  const [title, setTitle] = useState();
  const gridRef = useRef();

  const [fieldDefs, setFieldDefs] = useState();
  const [exportReq, setExportReq] = useState();

  const viewerReqParam = (params) => ({
    ...navState.viewerReqParam,
    entity: navState.entity,
    viewerPluginCode: 'REALLOC_CALC_TRX_VIEWER_PLUGIN',
    ...params,
  });

  useEffect(() => {
    rsrc
      .data(module, 'VIEWER_DATA_PLUGIN', viewerReqParam({ operation: 'FIELD_DEFS' }), navState.entity)
      .then((fieldDefs) => {
        setFieldDefs(fieldDefs);
      });

    const { trxSrcCode, orig, data } = navState.viewerReqParam;
    const scope = orig ? 'Original' : 'Reallocated';
    setTitle(`${scope} ${trxSrcCode} Transactions${!orig && data?.ndc11 ? ' for NDC: ' + data.ndc11 : ''}`);
  }, []);

  const onGridReady = (params) => {
    const dataSource = {
      getRows: (params) => {
        const expr = ExprUtils.filterModelToExpr(params.filterModel, fieldDefs);

        const reqParam = viewerReqParam({
          operation: 'ROWS',
          endRow: params.endRow,
          expr,
          rowsReq: {
            startRow: params.startRow,
            endRow: params.endRow,
            expr,
            sortModel: params.sortModel,
          },
        });

        buildAndSetExportReq(reqParam);

        rsrc.data(module, 'VIEWER_DATA_PLUGIN', reqParam, navState.entity).then((res) => {
          gridRef.current.onGridSuccess(res, params);
          gridRef.current.resize();
        });

        // Count and totals depend only on the filter, not the scrolled page, so fetch them once
        // per filter (first block) instead of on every infinite-scroll block.
        if (params.startRow === 0) {
          const summaryReqParam = viewerReqParam({ operation: 'AGGREGATES', rowsReq: { expr } });
          rsrc.data(module, 'VIEWER_DATA_PLUGIN', summaryReqParam, navState.entity).then((summary) => {
            gridRef.current.setRowCount(summary.count);
            gridRef.current.setTotalsRow(summary.totals);
          });
        }
      },
    };

    gridRef.current.setDataSource(dataSource);
  };

  const buildAndSetExportReq = (reqParam) => {
    const exportReq = {
      timeZoneName: DateUtils.timeZoneName(),
      name: navState.title + ' Export',
      module,
      entity: 'REALLOC_RATIO',
      uiType: 'VIEWER',
      viewerPluginCode: 'REALLOC_CALC_TRX_VIEWER_PLUGIN',
      viewerReqParam: reqParam,
      fieldDefs,
    };

    setExportReq(exportReq);
  };

  return (
    fieldDefs && (
      <>
        <Box
          sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
        >
          <Title icon={fas.faListUl} title={title} />
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ExportButton exportReq={exportReq} />
          </Box>
        </Box>
        <Grid key={'realloc'} ref={gridRef} gridOptions={{ fieldDefs }} events={{ onGridReady }} />
      </>
    )
  );
};
